import {urlPrefixed} from '../forgeApi';

/**
 * Browser half of the CLI's Authorization Code + PKCE login; the handlers are
 * in `services/gorilla/api/handler/cli_login.go`. The W&B app has the same
 * logic for deployments without a shell (`frontends/app/src/pages/CLILogin`).
 */

export const authorizeEndpoint = (): string =>
  urlPrefixed('/oidc/cli_login/authorize');

export const LOGIN_ERROR =
  "We couldn't start this login. Return to your terminal and run `wandb login` again.";
const LOGIN_REQUIRED_MESSAGE = 'You must be signed in to authorize the CLI.';
const CONSENT_HINTS = [
  LOGIN_REQUIRED_MESSAGE,
  'This endpoint may only be used by the W&B CLI.',
  'Only a browser login can authorize the CLI.',
  'You cannot authorize the CLI while impersonating another user.',
  'This account cannot authorize the CLI.',
];

export class ConsentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConsentError';
  }

  /**
   * Gorilla had no session for the request. Matched on the hint rather than
   * the status: `invalid_client` is also a 401.
   */
  get needsLogin(): boolean {
    return this.message.includes(LOGIN_REQUIRED_MESSAGE);
  }
}

const CLI_PARAMS = [
  'client_id',
  'response_type',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
] as const;

export type OAuthParams = Record<(typeof CLI_PARAMS)[number], string>;

/** The authorization request on the query string, or `null` if any part is missing. */
export function parseOAuthParams(search: string): OAuthParams | null {
  const query = new URLSearchParams(search);
  const params: Partial<OAuthParams> = {};
  for (const name of CLI_PARAMS) {
    const value = query.get(name);
    if (!value) {
      return null;
    }
    params[name] = value;
  }
  return params as OAuthParams;
}

export type ConsentDetails = {
  /**
   * The deployment's single organization on Dedicated; empty on multi-tenant,
   * where the viewer chooses from `organizations`.
   */
  organization: string;
  organizations: string[];
  /** One-time token bound to the browser session; the authorize POST consumes it. */
  consentToken: string;
};

const isConsentDetails = (details: unknown): details is ConsentDetails => {
  if (typeof details !== 'object' || details == null) {
    return false;
  }
  const {organization, organizations, consentToken} = details as Record<
    string,
    unknown
  >;
  return (
    typeof organization === 'string' &&
    Array.isArray(organizations) &&
    organizations.every(item => typeof item === 'string') &&
    typeof consentToken === 'string' &&
    consentToken !== ''
  );
};

/** Only Gorilla's own hints are shown; fosite echoes request parameters into its errors. */
async function failureMessage(response: Response): Promise<string> {
  const body: unknown = await response.json().catch(() => null);
  const error =
    typeof body === 'object' && body != null && 'error' in body
      ? String(body.error)
      : '';
  return CONSENT_HINTS.find(hint => error.includes(hint)) ?? LOGIN_ERROR;
}

export async function fetchConsent(
  params: OAuthParams
): Promise<ConsentDetails> {
  const query = new URLSearchParams(params);
  let response: Response;
  try {
    response = await fetch(urlPrefixed(`/oidc/cli_login/consent?${query}`), {
      credentials: 'include',
    });
  } catch {
    throw new ConsentError(LOGIN_ERROR);
  }
  if (!response.ok) {
    throw new ConsentError(await failureMessage(response));
  }
  const details: unknown = await response.json().catch(() => null);
  if (!isConsentDetails(details)) {
    throw new ConsentError(LOGIN_ERROR);
  }
  return details;
}
